// Column visibility preferences for the inventory table.
// Adapted from service-voyager-insured's useColumnPreferences hook.

import { useCallback, useEffect, useState } from 'react';

export type ColumnKey =
  | 'itemNumber'
  | 'brand'
  | 'year'
  | 'price'
  | 'condition'
  | 'category'
  | 'resolution'
  | 'quantity'
  | 'purchaseUrl'
  | 'distribution'
  | 'salesPrice'
  | 'deliveryFee'
  | 'installationFee'
  | 'salesTax'
  | 'expectedYears'
  | 'depreciationPercentage'
  | 'depreciationAmount'
  | 'actualCashValue';

/**
 * Columns the user can drag to resize. `name` is not a toggleable `ColumnKey`
 * — it is always shown — but it is the one most worth widening, so it resizes
 * alongside the optional columns.
 */
export type ResizableColumn = ColumnKey | 'name';

/**
 * The inventory page's columns. Sales price and its distribution lead, right
 * after the name, since what the replacement costs is what a reviewer checks
 * first.
 */
export const ALL_COLUMNS: ColumnKey[] = [
  'salesPrice',
  'distribution',
  'itemNumber',
  'brand',
  'condition',
  'category',
  'year',
  'quantity',
  'resolution',
  'purchaseUrl',
];

/**
 * The evaluation page's columns: the valuation worksheet, left to right from
 * the replacement's price to what the item is worth today. No item action
 * column, since the page is already narrowed to one action.
 */
export const EVALUATION_COLUMNS: ColumnKey[] = [
  'itemNumber',
  'brand',
  'condition',
  'category',
  'purchaseUrl',
  'salesPrice',
  'deliveryFee',
  'installationFee',
  'salesTax',
  'price',
  'distribution',
  'year',
  'expectedYears',
  'depreciationPercentage',
  'depreciationAmount',
  'actualCashValue',
];

/**
 * Starting width in px for each resizable column. `name` is absent on purpose:
 * until the user drags it, it stays flexible and absorbs the leftover width.
 */
export const DEFAULT_COLUMN_WIDTHS: Record<ColumnKey, number> = {
  itemNumber: 100,
  brand: 140,
  condition: 140,
  category: 160,
  year: 100,
  quantity: 100,
  resolution: 140,
  price: 200,
  purchaseUrl: 140,
  distribution: 120,
  salesPrice: 140,
  deliveryFee: 140,
  installationFee: 150,
  salesTax: 140,
  expectedYears: 200,
  depreciationPercentage: 240,
  depreciationAmount: 230,
  actualCashValue: 160,
};

/** Floor for a dragged column, so a column can never be dragged out of existence. */
export const MIN_COLUMN_WIDTH = 72;

/**
 * The name column holds a 56px thumbnail plus two lines of text. At common
 * desktop widths the fixed columns leave it barely more than the thumbnail, so
 * this floor is what keeps item names readable before anyone drags anything —
 * the table scrolls horizontally rather than starving it.
 */
export const MIN_NAME_COLUMN_WIDTH = 280;

export const getMinColumnWidth = (column: ResizableColumn) =>
  column === 'name' ? MIN_NAME_COLUMN_WIDTH : MIN_COLUMN_WIDTH;

export const COLUMN_LABELS: Record<ColumnKey, string> = {
  itemNumber: 'Item #',
  condition: 'Condition',
  category: 'Category',
  price: 'RCV',
  resolution: 'Item action',
  brand: 'Brand',
  year: 'Age',
  quantity: 'Quantity',
  purchaseUrl: 'Replacement',
  distribution: 'Distribution',
  salesPrice: 'Sales price',
  deliveryFee: 'Delivery fee',
  installationFee: 'Installation fee',
  salesTax: 'Sales tax',
  expectedYears: 'Useful lifespan (years)',
  depreciationPercentage: 'Total depreciation percentage',
  depreciationAmount: 'Total depreciation amount',
  actualCashValue: 'Actual cash value',
};

/** One table's columns, their labels, and where its preferences are saved. */
export type ColumnSet = {
  columns: ColumnKey[];
  labels: Record<ColumnKey, string>;
  storageKey: string;
};

export const INVENTORY_COLUMN_SET: ColumnSet = {
  columns: ALL_COLUMNS,
  labels: COLUMN_LABELS,
  storageKey: 'adjuster-inventory-columns',
};

export const EVALUATION_COLUMN_SET: ColumnSet = {
  columns: EVALUATION_COLUMNS,
  labels: { ...COLUMN_LABELS, purchaseUrl: 'Merchant link', year: 'Purchase year' },
  storageKey: 'adjuster-evaluation-columns',
};

// Nothing hidden by default: an employee who doesn't need a column (e.g. Item #
// on a job with no spreadsheet import) can hide it themselves.
const DEFAULT_HIDDEN: ColumnKey[] = [];

type ColumnWidths = Partial<Record<ResizableColumn, number>>;

// Column order is not saved: nothing lets a user reorder columns, so the order
// is always the column set's own, and a change to it reaches everyone at once.
// Prefs saved before this still carry a `columnOrder`, which is ignored.
interface StoredPrefs {
  hiddenColumns: ColumnKey[];
  columnWidths?: ColumnWidths;
}

function loadPrefs(storageKey: string): StoredPrefs | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as StoredPrefs;
  } catch {
    return null;
  }
}

function savePrefs(storageKey: string, prefs: StoredPrefs) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(prefs));
  } catch {
    /* quota exceeded — silently skip */
  }
}

/**
 * A saved column order with any column added to `columns` since it was saved
 * put back in: right after the column it follows in `columns`, or first when
 * it leads.
 *
 * No longer called: column order is not saved any more (see StoredPrefs). Kept
 * only because its QA-plan tests still reference it.
 */
export function withMissingColumns(storedOrder: ColumnKey[], columns: ColumnKey[]): ColumnKey[] {
  const order = storedOrder.filter((c) => columns.includes(c));
  columns.forEach((column, index) => {
    if (order.includes(column)) return;
    const previous = index > 0 ? columns[index - 1] : null;
    order.splice(previous === null ? 0 : order.indexOf(previous) + 1, 0, column);
  });
  return order;
}

export function useColumnPreferences({ columns, storageKey }: ColumnSet = INVENTORY_COLUMN_SET) {
  const stored = loadPrefs(storageKey);

  const columnOrder = columns;
  const [hiddenColumns, setHiddenColumns] = useState<Set<ColumnKey>>(new Set(stored?.hiddenColumns ?? DEFAULT_HIDDEN));
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(stored?.columnWidths ?? {});

  useEffect(() => {
    savePrefs(storageKey, { hiddenColumns: Array.from(hiddenColumns), columnWidths });
  }, [storageKey, hiddenColumns, columnWidths]);

  const visibleColumns = columnOrder.filter((c) => !hiddenColumns.has(c));

  const toggleColumn = useCallback((key: ColumnKey) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const setColumnWidth = useCallback((key: ResizableColumn, width: number) => {
    setColumnWidths((prev) => ({ ...prev, [key]: Math.max(getMinColumnWidth(key), Math.round(width)) }));
  }, []);

  const resetToDefaults = () => {
    setHiddenColumns(new Set(DEFAULT_HIDDEN));
    setColumnWidths({});
  };

  return {
    visibleColumns,
    columnOrder,
    hiddenColumns,
    columnWidths,
    toggleColumn,
    setColumnWidth,
    resetToDefaults,
  };
}
